import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface BillingTransaction {
  id: number;
  tenant_id: number;
  product_id: number | null;
  loan_type: string | null;
  pd_session_code: string | null;
  amount: number;
  currency: string;
  status: 'PENDING_INVOICE' | 'INVOICED' | 'VOID';
  occurred_at: string;
  product: { name: string; slug: string } | null;
}

export interface BillingSummary {
  tenant_id: number;
  pending_total: number;
  pending_count: number;
}

export interface InvoiceLineItem {
  id: number;
  description: string;
  quantity: number;
  unit_rate: number;
  amount: number;
  product?: { name: string; slug: string } | null;
}

export interface InvoiceRecord {
  id: number;
  invoice_number: string;
  tenant_id: number;
  period_start: string;
  period_end: string;
  subtotal: number;
  gst_percent: number | null;
  gst_amount: number;
  total: number;
  currency: string;
  status: 'DRAFT' | 'SENT' | 'PENDING_VERIFICATION' | 'PAID' | 'VOID';
  paid_amount: number;
  /** Server-derived outstanding amount; present on list responses. */
  balance?: number;
  due_date?: string | null;
  payment_status: 'PENDING' | 'PARTIALLY_PAID' | 'PAID';
  notes: string | null;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  tenant?: { tenant_name: string | null; tenant_subdomain: string | null };
  line_items?: InvoiceLineItem[];
}

export interface PendingPayment {
  id: number;
  invoice_id: number;
  tenant_id: number;
  amount: number;
  currency: string;
  receipt: string;
  offline_method: string | null;
  reference: string | null;
  paid_date: string | null;
  status: string;
  created_at: string;
  tenant?: { tenant_name: string | null; tenant_subdomain: string | null };
}

export interface PaymentRow {
  id: number;
  invoice_id: number;
  tenant_id: number;
  method: string; // 'razorpay' | 'offline'
  status: string;
  amount: number;
  amount_minor: number | null;
  currency: string;
  receipt: string; // invoice number
  provider: string | null;
  provider_order_id: string | null;
  provider_payment_id: string | null;
  signature: string | null;
  offline_method: string | null;
  reference: string | null;
  receipt_path: string | null;
  paid_date: string | null;
  verified_by: number | null;
  verified_at: string | null;
  error_reason: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  tenant?: { tenant_name: string | null; tenant_subdomain: string | null };
  invoice?: {
    id: number;
    invoice_number: string | null;
    invoice_type: string | null;
    total: number;
    paid_amount: number;
    balance: number;
    subscription?: { subscription_name: string | null } | null;
  } | null;
}

export interface BusinessInvoiceRow {
  tenant_id: number;
  display_id: string;
  business: string;
  total_invoices: number;
  total: number;
  paid_amount: number;
  balance: number;
  payment_type: string;
  issued_date: string | null;
}

export interface InvoiceDashboard {
  active_businesses: number;
  invoice_raised: number;
  invoice_collected: number;
  businesses: BusinessInvoiceRow[];
}

export interface PaymentHistoryRow {
  sr_no: number;
  reference: string | null;
  paid_date: string | null;
  amount_received: number;
  tds_amount: number;
  total: number;
}

export interface RecordPaymentBody {
  reference: string;
  account_holder_name: string;
  account_number: string;
  paid_amount: number;
  payment_date: string;
  tds_section?: string;
}

export interface PaymentListQuery {
  status?: string;
  tenant_id?: number;
  method?: string;
  page?: number;
  page_size?: number;
}

@Injectable({ providedIn: 'root' })
export class BillingApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  listTransactions(params: { tenant_id?: number; status?: string; page?: number; page_size?: number }) {
    const q = new URLSearchParams();
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.status) q.set('status', params.status);
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    return this.http.get<{ data: { items: BillingTransaction[]; total: number } }>(`${this.base}/billing/transactions?${q}`);
  }

  tenantSummary(tenantId: number) {
    return this.http.get<{ data: BillingSummary }>(`${this.base}/billing/tenants/${tenantId}/summary`);
  }

  generateInvoice(tenantId: number, body: { period_start: string; period_end: string; gst_percent?: number; notes?: string }) {
    return this.http.post<{ data: InvoiceRecord; message: string }>(`${this.base}/billing/tenants/${tenantId}/invoices`, body);
  }

  listInvoices(params: { tenant_id?: number; status?: string; page?: number; page_size?: number }) {
    const q = new URLSearchParams();
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.status) q.set('status', params.status);
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    return this.http.get<{ data: { items: InvoiceRecord[]; total: number } }>(`${this.base}/billing/invoices?${q}`);
  }

  getInvoice(id: number) {
    return this.http.get<{ data: InvoiceRecord }>(`${this.base}/billing/invoices/${id}`);
  }

  /** The invoice PDF as it would be emailed (inline preview). */
  getInvoicePdf(id: number) {
    return this.http.get(`${this.base}/billing/invoices/${id}/pdf`, { responseType: 'blob' });
  }

  /** A PDF of the invoice that would be generated for this period; creates nothing. */
  previewDraftInvoice(body: { tenant_id: number; period_start: string; period_end: string; gst_percent?: number; notes?: string }) {
    return this.http.post(`${this.base}/billing/invoices/preview-draft`, body, { responseType: 'blob' });
  }

  sendInvoice(id: number, to?: string) {
    return this.http.post<{ message: string }>(`${this.base}/billing/invoices/${id}/send`, to ? { to } : {});
  }

  voidInvoice(id: number) {
    return this.http.patch<{ message: string }>(`${this.base}/billing/invoices/${id}/void`, {});
  }

  // Offline payments awaiting operator verification.
  pendingPayments() {
    return this.http.get<{ data: { items: PendingPayment[] } }>(
      `${this.base}/billing/payments?status=PENDING_VERIFICATION&method=offline&page_size=100`,
    );
  }

  // Presigned URL to view a payment's uploaded receipt.
  paymentReceipt(id: number) {
    return this.http.get<{ data: { url: string } }>(`${this.base}/billing/payments/${id}/receipt`);
  }

  // Confirm a payment.
  confirmPayment(id: number) {
    return this.http.patch<{ message: string }>(`${this.base}/billing/payments/${id}/confirm`, {});
  }

  // Reject a payment.
  rejectPayment(id: number, reason: string) {
    return this.http.patch<{ message: string }>(`${this.base}/billing/payments/${id}/reject`, { reason });
  }

  // Full payment ledger (all statuses/methods) with optional filters + pagination.
  listPayments(q: PaymentListQuery) {
    const params = new URLSearchParams();
    if (q.status) params.set('status', q.status);
    if (q.tenant_id) params.set('tenant_id', String(q.tenant_id));
    if (q.method) params.set('method', q.method);
    if (q.page) params.set('page', String(q.page));
    if (q.page_size) params.set('page_size', String(q.page_size));
    return this.http.get<{ data: { items: PaymentRow[]; total: number; page: number; page_size: number } }>(
      `${this.base}/billing/payments?${params.toString()}`,
    );
  }

  getInvoiceDashboard() {
    return this.http.get<{ data: InvoiceDashboard }>(`${this.base}/billing/dashboard`);
  }

  getPaymentHistory(invoiceId: number) {
    return this.http.get<{
      data: {
        items: PaymentHistoryRow[];
        grand_total_received: number;
        grand_total_tds: number;
        grand_total: number;
      };
    }>(`${this.base}/billing/invoices/${invoiceId}/payments`);
  }

  // Record an offline payment (with optional TDS) against an invoice.
  recordPayment(invoiceId: number, body: RecordPaymentBody) {
    return this.http.post<{ data: { invoice: { paid_amount: number; balance: number; payment_status: string } } }>(
      `${this.base}/billing/invoices/${invoiceId}/record-payment`,
      body,
    );
  }
}
